// Sync-project-tables modal (Database Designer).
//
// Deploys every transaction object's tables to the project's current
// preview/run database, one object at a time, with a live progress bar and
// per-object status. Opened automatically after the project's database is
// changed (so the new database gets the schema) or manually from the
// "Sync tables" button on the database screen's connection bar.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertTriangle, CheckCircle2, Database, Loader2, XCircle,
} from 'lucide-react'
import Modal from '@/components/common/Modal'
import { listDeployableObjects } from '@/api/deploy'
import { livePreviewDeploy } from '@/livepreview/api'

type RowStatus = 'pending' | 'syncing' | 'done' | 'warning' | 'error'

interface SyncRow {
  obj: string
  status: RowStatus
  detail?: string
}

function statusIcon(status: RowStatus) {
  switch (status) {
    case 'syncing':
      return <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
    case 'done':
      return <CheckCircle2 size={14} style={{ color: '#22c55e' }} />
    case 'warning':
      return <AlertTriangle size={14} style={{ color: '#f59e0b' }} />
    case 'error':
      return <XCircle size={14} style={{ color: '#ef4444' }} />
    default:
      return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: 'var(--color-border)' }} />
  }
}

export default function SyncTablesModal({
  project, open, onClose, intro, targetSummary,
}: {
  project: string
  open: boolean
  onClose: () => void
  /** Optional banner shown above the list (e.g. after a DB change). */
  intro?: string
  /** Human-readable target DB the tables are synced to. */
  targetSummary?: string
}) {
  const [rows, setRows] = useState<SyncRow[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const cancelRef = useRef(false)

  // Load the project's transaction objects whenever the modal opens.
  useEffect(() => {
    if (!open) return
    cancelRef.current = false
    setRunning(false)
    setFinished(false)
    setRows([])
    setLoading(true)
    listDeployableObjects(project)
      .then((objs) => {
        const txns = (objs || []).filter((o) => o.obj_type === 'T')
        setRows(txns.map((o) => ({ obj: o.obj_name, status: 'pending' as RowStatus })))
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, project])

  const runSync = useCallback(async () => {
    setRunning(true)
    setFinished(false)
    cancelRef.current = false
    // Snapshot the object names up-front so the loop is stable while state
    // updates re-render the list.
    const names = rows.map((r) => r.obj)
    for (let i = 0; i < names.length; i += 1) {
      if (cancelRef.current) break
      setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'syncing', detail: undefined } : r)))
      try {
        const res = await livePreviewDeploy(project, names[i])
        const tables = res?.sync?.tables ?? []
        const syncWarnings = res?.sync?.warnings ?? []
        const fnErrors = (res?.functions ?? []).filter((f) => f.status === 'error')
        const created = tables.filter((t) => t.status === 'created').length
        const altered = tables.filter((t) => t.status === 'altered').length
        const parts: string[] = []
        if (created) parts.push(`${created} created`)
        if (altered) parts.push(`${altered} updated`)
        if (!created && !altered) parts.push(`${tables.length} table${tables.length === 1 ? '' : 's'} up to date`)
        const warnCount = syncWarnings.length + fnErrors.length
        if (warnCount) parts.push(`${warnCount} warning${warnCount === 1 ? '' : 's'}`)
        setRows((prev) => prev.map((r, idx) => (idx === i
          ? { ...r, status: warnCount ? 'warning' : 'done', detail: parts.join(' · ') }
          : r)))
      } catch (err: unknown) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        const msg = detail || (err instanceof Error ? err.message : 'Sync failed')
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'error', detail: String(msg).slice(0, 200) } : r)))
      }
    }
    setRunning(false)
    setFinished(true)
  }, [rows, project])

  const handleClose = () => {
    cancelRef.current = true
    onClose()
  }

  const total = rows.length
  const completed = rows.filter((r) => r.status === 'done' || r.status === 'warning' || r.status === 'error').length
  const errors = rows.filter((r) => r.status === 'error').length
  const warnings = rows.filter((r) => r.status === 'warning').length
  const pct = total ? Math.round((completed / total) * 100) : 0

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Sync project tables"
      size="wide"
      footer={(
        <>
          <button className="btn btn-ghost btn-sm" onClick={handleClose} disabled={running}>
            {finished ? 'Close' : 'Cancel'}
          </button>
          {!finished && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void runSync()}
              disabled={running || loading || total === 0}
            >
              {running ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Syncing… ({completed}/{total})
                </>
              ) : (
                <>
                  <Database size={14} />
                  {`Sync ${total} table set${total === 1 ? '' : 's'}`}
                </>
              )}
            </button>
          )}
        </>
      )}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {intro && (
          <div style={{
            padding: '8px 12px', borderRadius: 8, fontSize: '0.875rem',
            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
            color: 'var(--color-text)',
          }}>
            {intro}
          </div>
        )}

        <div style={{
          padding: '8px 12px', borderRadius: 8, fontSize: '0.875rem',
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Database size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <span><strong>Target:</strong> {targetSummary || 'Project database (preview & run)'}</span>
        </div>

        {/* Progress bar */}
        {(running || finished) && total > 0 && (
          <div>
            <div style={{
              height: 8, borderRadius: 999, overflow: 'hidden',
              background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
            }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: errors ? '#f59e0b' : 'var(--color-accent)',
                transition: 'width 0.25s ease',
              }} />
            </div>
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              {completed}/{total} done{warnings ? ` · ${warnings} with warnings` : ''}{errors ? ` · ${errors} failed` : ''}
            </div>
          </div>
        )}

        {loading ? (
          <div className="spinner" />
        ) : total === 0 ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            No transaction objects to sync. Tables are created from transaction
            designs — add a transaction first.
          </p>
        ) : (
          <div style={{
            display: 'grid', gap: 0, border: '1px solid var(--color-border)',
            borderRadius: 8, overflow: 'hidden', maxHeight: 320, overflowY: 'auto',
          }}>
            {rows.map((r, i) => (
              <div
                key={r.obj}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 12px', fontSize: '0.875rem',
                  borderBottom: i < rows.length - 1 ? '1px solid var(--color-border)' : 'none',
                  background: r.status === 'syncing' ? 'var(--color-bg-secondary)' : 'transparent',
                }}
              >
                <span style={{ width: 16, display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                  {statusIcon(r.status)}
                </span>
                <span style={{ fontWeight: 500, flexShrink: 0 }}>{r.obj}</span>
                <span style={{
                  marginLeft: 'auto', fontSize: '0.8125rem', textAlign: 'right',
                  color: r.status === 'error' ? '#ef4444' : 'var(--color-text-secondary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%',
                }} title={r.detail || ''}>
                  {r.detail || (r.status === 'pending' ? 'Waiting' : '')}
                </span>
              </div>
            ))}
          </div>
        )}

        {finished && (
          <div style={{ fontSize: '0.875rem', color: errors ? '#ef4444' : '#22c55e', fontWeight: 500 }}>
            {errors
              ? `Sync finished with ${errors} failure${errors === 1 ? '' : 's'}. Re-run to retry.`
              : `All ${total} table set${total === 1 ? '' : 's'} synced to the database.`}
          </div>
        )}
      </div>
    </Modal>
  )
}
